import { useCallback, useEffect, useState } from "react";
import { api, notifyError } from "@/lib/api";
import { Plus, Trash, FileArrowDown } from "@phosphor-icons/react";
import { downloadCsv } from "./PreTradingTab";
import RuleNote from "./RuleNote";

const CURRENCIES = ["USD", "EUR", "GBP"];

export default function UsMileageTab({ onChange }) {
  const [data, setData] = useState({ items: [], ytd_miles: 0, ytd_usd: 0, total_usd: 0, unreported_count: 0 });
  const [rate, setRate] = useState(null);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), purpose: "", from_location: "", to_location: "", mode: "standard", distance_miles: "", fuel_amount: "", fuel_currency: "USD", reported_irs: false });

  const load = useCallback(async () => { const r = await api.get("/compliance/us-mileage"); setData(r.data); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { (async () => { try { const r = await api.get("/compliance/rates"); setRate(r.data.us?.mileage?.current_rate); } catch (e) { console.debug("mileage rate fetch failed", e); } })(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { date: form.date, purpose: form.purpose, from_location: form.from_location, to_location: form.to_location, mode: form.mode, reported_irs: form.reported_irs };
      if (form.mode === "standard") payload.distance_miles = Number(form.distance_miles || 0);
      else { payload.fuel_amount = Number(form.fuel_amount || 0); payload.fuel_currency = form.fuel_currency; }
      await api.post("/compliance/us-mileage", payload);
      setForm({ ...form, purpose: "", from_location: "", to_location: "", distance_miles: "", fuel_amount: "" });
      load(); onChange && onChange();
    } catch (err) { notifyError(err); }
  };

  const toggle = async (id) => { await api.put(`/compliance/us-mileage/${id}/reported`); load(); onChange && onChange(); };
  const del = async (id) => { await api.delete(`/compliance/us-mileage/${id}`); load(); onChange && onChange(); };

  return (
    <div className="space-y-6">
      <div className="glass rounded-2xl p-6" data-testid="us-mileage-tab">
        <div className="flex items-center gap-2 eyebrow mb-1">
          <span className="px-2 py-0.5 rounded-full text-[9px] font-bold" style={{ background: "#C77DFF22", color: "#C77DFF", border: "1px solid #C77DFF" }}>US</span>
          Log a trip · IRS standard mileage{rate ? ` (${(rate * 100).toFixed(1)}¢/mi today)` : ""}
        </div>
        <div className="text-xs text-white/45 mb-4">IRS business standard rate applies by trip date (72.5¢ Jan–Jun 2026, 76¢ Jul–Dec 2026). Or log <strong>actual fuel cost</strong> — foreign receipts convert to USD at the trip-date rate.</div>

        <div className="flex gap-2 mb-4">
          {["standard", "actual"].map((m) => (
            <button key={m} type="button" onClick={() => setForm({ ...form, mode: m })} data-testid={`usmil-mode-${m}`}
              className={`px-4 py-2 rounded-lg text-sm ${form.mode === m ? "bg-[#C77DFF]/15 text-[#C77DFF] border border-[#C77DFF]/50" : "bg-black/20 text-white/60 border border-white/8"}`}>
              {m === "standard" ? "Standard mileage" : "Actual fuel cost"}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
          <div><label>Date</label><input required type="date" data-testid="usmil-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
          <div className="md:col-span-2"><label>Purpose</label><input data-testid="usmil-purpose" value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} placeholder="e.g. Cleveland client visit" /></div>
          <div><label>From</label><input data-testid="usmil-from" value={form.from_location} onChange={(e) => setForm({ ...form, from_location: e.target.value })} /></div>
          <div><label>To</label><input data-testid="usmil-to" value={form.to_location} onChange={(e) => setForm({ ...form, to_location: e.target.value })} /></div>
          {form.mode === "standard" ? (
            <div><label>Distance (miles)</label><input required type="number" step="0.1" data-testid="usmil-miles" value={form.distance_miles} onChange={(e) => setForm({ ...form, distance_miles: e.target.value })} /></div>
          ) : (
            <>
              <div><label>Fuel amount</label><input required type="number" step="0.01" data-testid="usmil-fuel" value={form.fuel_amount} onChange={(e) => setForm({ ...form, fuel_amount: e.target.value })} /></div>
              <div><label>Currency</label><select data-testid="usmil-fuel-ccy" value={form.fuel_currency} onChange={(e) => setForm({ ...form, fuel_currency: e.target.value })}>{CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
            </>
          )}
          <button type="submit" data-testid="usmil-add" className="btn-gold"><Plus weight="bold" size={14} /> Log trip</button>
        </form>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-6 text-sm">
          <div><span className="text-white/50">YTD miles:</span> <span className="mono" data-testid="usmil-ytd-miles">{Number(data.ytd_miles).toLocaleString()} mi</span></div>
          <div><span className="text-white/50">YTD claim:</span> <span className="mono text-[#FFB020]" data-testid="usmil-ytd-usd">${Number(data.ytd_usd).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></div>
        </div>
        <button onClick={() => downloadCsv("/compliance/us-mileage.csv", "us-mileage-log.csv")} data-testid="usmil-export" className="btn-ghost text-sm"><FileArrowDown size={16} /> Export CSV</button>
      </div>

      <div className="glass rounded-xl overflow-auto">
        <table className="data">
          <thead><tr><th>Date</th><th>Purpose</th><th>Method</th><th className="text-right">Miles</th><th className="text-right">Rate</th><th className="text-right">Amount (USD)</th><th>IRS</th><th /></tr></thead>
          <tbody>
            {data.items.length === 0 ? (
              <tr><td colSpan="8" className="text-center text-white/40 py-8">No trips logged.</td></tr>
            ) : data.items.map((e) => (
              <tr key={e.entry_id} data-testid={`usmil-row-${e.entry_id}`}>
                <td className="mono text-xs">{e.date}</td>
                <td>{e.purpose || "—"}</td>
                <td className="text-white/70 text-xs">{e.mode === "actual" ? `Actual (${e.fuel_original_amount} ${e.fuel_currency})` : "Standard"}</td>
                <td className="num">{e.mode === "actual" ? "—" : Number(e.distance_miles).toFixed(1)}</td>
                <td className="num text-white/60">{e.irs_rate ? `${(e.irs_rate * 100).toFixed(1)}¢` : "—"}</td>
                <td className="num text-[#FFB020]">${Number(e.amount_usd).toFixed(2)}</td>
                <td>
                  <button onClick={() => toggle(e.entry_id)} data-testid={`usmil-irs-${e.entry_id}`} className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider ${e.reported_irs ? "badge-pass" : "badge-warn"}`}>{e.reported_irs ? "Reported" : "Pending"}</button>
                </td>
                <td><button onClick={() => del(e.entry_id)} className="text-white/40 hover:text-[#E63946]" data-testid={`usmil-del-${e.entry_id}`}><Trash size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <RuleNote id="us_mileage" />
    </div>
  );
}
